/**
 * /app/$tenantSlug/my-time — My Time (unified Attendance + Timesheet,
 * docs/47 P1).
 *
 * Collapses the two former employee-facing time surfaces — Attendance
 * (punch in/out) and Timesheet (weekly project grid) — into ONE page with
 * three zones. They remain two LAYERS (presence vs project allocation), not
 * two modules:
 *
 *   Zone A — Today (ALWAYS):       the punch card (PunchCard). Punch in/out,
 *                                  geo/selfie, today's status, offline queue.
 *   Zone B — Project hours (IF):   WeekHours — rendered ONLY when the
 *                                  tenant has at least one active,
 *                                  non-internal (real client) project. A
 *                                  week of hours with auto-save; Submit
 *                                  Week (loose/strict aware).
 *   Zone C — History / week grid:  compact day strip + a "View week grid"
 *                                  link to the existing drill-down weekly
 *                                  submit/approve grid.
 *
 * The missed-day card (the most recent working day with no punch and no
 * leave, or ?day=YYYY-MM-DD from the next-morning notification) offers
 * I Was Working (a small dialog to your manager) and Apply for Leave.
 *
 * RequireFeature("time_tracking") is the server boundary on every call; the
 * shared axios interceptor turns a 403 upgrade_required into a /pricing
 * redirect. The page is reachable by every authenticated role (employees
 * punch + log; approvals live in the Inbox, the team view in Manage).
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { X } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { PunchCard } from "@/components/time/punch-card";
import { WeekHours } from "@/components/time/week-hours";
import { timesheetKeys, useProjects } from "@/hooks/use-timesheet";
import { attendanceKeys, useMyCalendarDays, useToday } from "@/hooks/use-attendance";
import { attendanceApi, timesheetApi } from "@/lib/api";
import { AttendanceDayChips } from "@/components/time/day-chips";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { todayISO } from "@/lib/timesheet-format";
import { missingDayPrompt, parseDayParam } from "@/lib/attendance-register";
import { IWasWorkingDialog } from "@/components/time/i-was-working-dialog";
import { useMyMonth } from "@/hooks/use-attendance-register";
import { monthOf } from "@/lib/my-month";
import { loggedHoursLine } from "@/lib/work-from";
import { pickMissedDay, urlMissedDay } from "@/lib/week-hours";
import { punchNeededOn } from "@/lib/punching";

const searchSchema = z.object({
  day: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/my-time")({
  validateSearch: searchSchema,
  // A9 hover-prefetch. Primary = the punch card's "today" read (Zone A,
  // always rendered). Secondary (fire-and-forget): the projects list that
  // decides Zone B's disclosure, and the last-14-days strip (same UTC
  // date arithmetic as the component's `last14` memo so the cache key
  // matches). The timesheet-settings read stays component-owned — it is
  // gated on a real client project existing.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: timesheetKeys.projectsList(undefined),
      queryFn: ({ signal }) => timesheetApi.listProjects(undefined, signal),
    });
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 13);
    const fromISO = from.toISOString().slice(0, 10);
    const toISO = to.toISOString().slice(0, 10);
    void context.queryClient.prefetchQuery({
      queryKey: [...attendanceKeys.myDays(fromISO, toISO), "calendar"] as const,
      queryFn: ({ signal }) =>
        attendanceApi.listMyCalendarDays({ from: fromISO, to: toISO }, signal),
    });
    return context.queryClient
      .ensureQueryData({
        queryKey: attendanceKeys.today(),
        queryFn: ({ signal }) => attendanceApi.getToday(signal),
      })
      .catch(() => undefined);
  },
  component: MyTimeRoute,
});

function MyTimeRoute() {
  const params = Route.useParams();
  const missingDay = parseDayParam(Route.useSearch().day);


  // Zone B disclosure: the project band renders iff the tenant has at least
  // one active, NON-internal (real client) project. Seeded internal
  // categories (GENERAL/MEETINGS/TRAINING/ADMIN, is_internal=true) do NOT
  // count. A pure-attendance tenant sees only the punch spine.
  const projects = useProjects();
  const hasRealProject = useMemo(
    () =>
      (projects.data?.items ?? []).some(
        (p) => p.is_active && !p.is_internal,
      ),
    [projects.data],
  );

  // Zone C — the SINGLE day strip on this page (2026-08 UX remediation:
  // the punch card's duplicate "Last 30 days" strip was removed). The
  // fortnight range is passed to AttendanceDayChips so every date
  // materialises. Holidays, weekly offs and approved leave / WFH without
  // an attendance record come back as synthetic days (QA 2026-10 leave
  // #13), so only a genuinely unrecorded working day reads "no record".
  const last14 = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 13);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  }, []);
  const days = useMyCalendarDays(last14.from, last14.to);

  // Missed-day card: ?day= (notification link) wins; otherwise the most
  // recent past working day in the last 14 with no punch and no leave, on
  // which a punch was required of the person (GET /attendance/today
  // `punching.required_days` — Punching Required, not "Doesn't need to
  // punch"; the server's PunchRequired rule).
  const today = useToday();
  const punching = today.data?.punching;
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => readDismissed());
  const autoMissed = useMemo(() => {
    if (!days.data || !today.data) return null;
    const t = todayISO();
    return pickMissedDay(days.data.days, last14.from, t, dismissed, (d) => punchNeededOn(punching, d, t));
  }, [days.data, today.data, punching, last14.from, dismissed]);
  // A stale notification link for a day fixed since then shows nothing.
  const urlDay = urlMissedDay(missingDay, days.data?.days, last14.from, todayISO());
  const shownDay = urlDay ?? autoMissed;

  return (
    <TimeAndLeaveShell
      title="Today"
      description={
        punching?.mode === "off"
          ? "Log your project hours and see your recent days."
          : "Punch in and out, and log your project hours."
      }
    >
      <div className="space-y-6">
        {shownDay && (
          <MissingDayCard
            day={shownDay}
            tenantSlug={params.tenantSlug}
            fromUrl={urlDay === shownDay}
            onDismiss={() => setDismissed(addDismissed(shownDay))}
          />
        )}

        {/* Zone A — Today (always). */}
        <PunchCard />

        {/* Zone B — Project time (only when a real client project exists). */}
        {hasRealProject && <WeekHours />}

        {/* Zone C — the last 14 days (the week's hours are edited above). */}
        <Card>
          <CardHeader>
            <CardTitle>This Fortnight</CardTitle>
          </CardHeader>
          <CardContent>
            {days.isLoading ? (
              <Skeleton className="h-7 w-full" />
            ) : days.data ? (
              <AttendanceDayChips
                days={days.data.days}
                from={last14.from}
                to={last14.to}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Could not load attendance for the last two weeks.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </TimeAndLeaveShell>
  );
}

const DISMISS_KEY = "wr.missedDay.dismissed";

/** Dismissed missed-day dates, remembered per browser. Storage may be
 *  blocked or empty; everything degrades to "not dismissed". */
function readDismissed(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function addDismissed(day: string): ReadonlySet<string> {
  const next = new Set(readDismissed());
  next.add(day);
  try {
    // Keep the newest 60 dates so the key cannot grow without bound.
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(next).sort().slice(-60)));
  } catch {
    /* storage unavailable: dismissed for this visit only */
  }
  return next;
}

/**
 * "Thu 29 Oct has no punch and no leave." (+ "You logged 8 h that day.")
 * Shown for the most recent missed working day, or the ?day= date from the
 * next-morning notification. I Was Working opens a small dialog; Apply for
 * Leave opens the leave form on that day; the x hides the card for that date.
 */
function MissingDayCard({
  day,
  tenantSlug,
  fromUrl,
  onDismiss,
}: Readonly<{ day: string; tenantSlug: string; fromUrl: boolean; onDismiss: () => void }>) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const month = useMyMonth(monthOf(day));
  const hours = loggedHoursLine(month.data?.days.find((d) => d.date === day)?.project_minutes ?? 0);
  return (
    <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40" data-testid="missing-day-card">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <p className="min-w-0 flex-1 text-sm font-medium">
          {missingDayPrompt(day)}
          {hours && <span className="font-normal"> {hours}</span>}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="min-h-11" onClick={() => setOpen(true)}>
            I Was Working
          </Button>
          <Button asChild variant="outline" className="min-h-11">
            <Link to="/app/$tenantSlug/leave/new" params={{ tenantSlug }} search={{ date: day }}>
              Apply for Leave
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label="Dismiss"
            onClick={() => {
              onDismiss();
              if (fromUrl) {
                void navigate({
                  to: "/app/$tenantSlug/my-time",
                  params: { tenantSlug },
                  search: {},
                  replace: true,
                });
              }
            }}
          >
            <X aria-hidden className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
      <IWasWorkingDialog day={day} open={open} onOpenChange={setOpen} />
    </Card>
  );
}
